// @flow
import React from 'react';
import classNames from 'classnames';
import { connect } from 'react-redux';
import * as cssUtils from 'app/utils/cssUtils';
import { debounce } from 'lodash';
import GroupsForFriendSelector from 'app/selectors/friends/groupsForFriendSelector';
import { updateFriendGroups } from 'app/actions/friendsActions';
import useSheet from 'app/jss';
import FriendGroupItem from './FriendGroupItem';
import GroupListNoGroups from './GroupListNoGroups';

type Props = {
  friendId: number | string,
  groups?: Array<FriendGroup>,
  sheet: Object,
  userGroups?: Set<FriendGroup>,
  onGroupsChange: (friendId: number, toAdd: Array<number>, toRemove: Array<number>) => void,
};

type State = {
  groupsToUpdate: Map<FriendGroup, boolean>,
};

class FriendGroupList extends React.Component {
  props: Props;
  state: State = {
    groupsToUpdate: new Map(),
  };

  static defaultProps = {
    groups: [],
    userGroups: new Set(),
  };

  saveGroups = debounce(() => {
    const { onGroupsChange, friendId, userGroups } = this.props;
    const { groupsToUpdate } = this.state;

    const toRemove = [];
    const toAdd = [];
    [...groupsToUpdate.entries()].forEach(([group, add]) => {
      if (add) {
        if (!userGroups.has(group)) {
          toAdd.push(group.id);
        }
      } else if (userGroups.has(group)) {
        toRemove.push(group.id);
      }
    });

    if (toAdd.length + toRemove.length > 0) {
      onGroupsChange(friendId, toAdd, toRemove);
    }

    // clear it, and then we wait for the next call to set state
    // from the store updating to show that it was updated
    // but by then, we should have our response and be in a consistant state
    groupsToUpdate.clear();
  }, 1000);

  onGroupSelect = (group: FriendGroup) => {
    const { userGroups } = this.props;
    const { groupsToUpdate } = this.state;

    const newVal = groupsToUpdate.has(group) ? !groupsToUpdate.get(group) : !userGroups.has(group);
    groupsToUpdate.set(group, newVal);

    this.saveGroups();
    this.setState({ groupsToUpdate });
  };

  buildGroups() {
    const { groups, userGroups, sheet: { classes } } = this.props;
    const { groupsToUpdate } = this.state;

    // Make sure selected ones go first
    return groups
      .map(group => ({
        // 'optimistically update' by also updating showing selected even though we
        // may not have sent it to the server yet
        selected:
          (userGroups.has(group) && !groupsToUpdate.has(group)) || groupsToUpdate.get(group),
        group,
      }))
      .sort((a, b) => (!a.selected && b.selected ? 1 : 0))
      .map((tuple) => {
        const groupClasses = classNames(classes.groupItem, {
          selected: tuple.selected,
        });

        return (
          <FriendGroupItem
            className={groupClasses}
            group={tuple.group}
            key={tuple.group.id}
            onClick={this.onGroupSelect}
            selected={tuple.selected}
          >
            {tuple.group.name}
          </FriendGroupItem>
        );
      });
  }

  render() {
    const { sheet: { classes }, groups } = this.props;

    return (
      <div className={classes.groupsList}>
        <div className={classes.groupListContent}>
          {groups.length > 0 && this.buildGroups()}
          {groups.length === 0 && <GroupListNoGroups />}
        </div>
      </div>
    );
  }
}

function applyStyles(theme: Theme) {
  return {
    groupsList: {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
    },
    groupListContent: {
      flexGrow: '1',
      overflow: 'auto',
      display: 'flex',
      flexDirection: 'column',
      background: theme.color.basis6,
    },
    newGroup: {
      color: theme.color.basis3,
      transition: 'color 0.2s',
      ...cssUtils.hover({
        color: theme.color.basis1,
      }),
      '& >:not(:last-child)': {
        marginRight: 10,
      },
    },
  };
}

function mapStateToProps(state, ownProps) {
  return {
    userGroups: GroupsForFriendSelector(ownProps.friendId, state),
  };
}

const actions = {
  onGroupsChange: updateFriendGroups,
};

export default connect(mapStateToProps, actions)(useSheet(applyStyles)(FriendGroupList));
